import React, { useState, useEffect } from 'react';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../utils/buildingSelection';
import { feeCategoriesApi, BuildingOption, BillingPeriod } from '../api/feeCategoriesApi';
import { MeterReadingType } from '../api/meterReadingsApi';
import { MeterReadingsPanel } from '../components/bulk-fee/MeterReadingsPanel';
import { UtilityMetersPanel } from '../components/bulk-fee/UtilityMetersPanel';

type BulkFeeTab =
  | 'water-readings'
  | 'electric-readings'
  | 'water-meters'
  | 'electric-meters';

const BulkFeeEntry: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [billingPeriods, setBillingPeriods] = useState<BillingPeriod[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod | null>(null);
  const [activeTab, setActiveTab] = useState<BulkFeeTab>('water-readings');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBuildings();
    loadBillingPeriods();
  }, []);

  const loadBuildings = async () => {
    try {
      const data = await feeCategoriesApi.getBuildingsForUser();
      setBuildings(data);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
    } catch {
      setError(t('failedToLoad'));
    }
  };

  const loadBillingPeriods = async () => {
    try {
      const data = await feeCategoriesApi.getBillingPeriods();
      setBillingPeriods(data);
      if (data.length > 0) setSelectedPeriod(data[0]);
    } catch {
      setError(t('failedToLoadBillingPeriods'));
    }
  };

  const selectedBuilding = buildings.find((b) => b.id === selectedBuildingId);
  const isReadingsTab = activeTab === 'water-readings' || activeTab === 'electric-readings';
  const readingType =
    activeTab === 'electric-readings' || activeTab === 'electric-meters'
      ? MeterReadingType.ELECTRICITY
      : MeterReadingType.WATER;

  const tabs: { id: BulkFeeTab; label: string }[] = [
    { id: 'water-readings', label: t('tabWaterReadings') },
    { id: 'electric-readings', label: t('tabElectricReadings') },
    { id: 'water-meters', label: t('tabWaterMeters') },
    { id: 'electric-meters', label: t('tabElectricMeters') },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('bulkFeeEntryTitle')}</h1>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {shouldShowBuildingSelector(buildings) && (
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-4">
          <label className="font-medium text-gray-700 whitespace-nowrap">{t('selectBuilding')}</label>
          <select
            value={selectedBuildingId}
            onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0 flex-1 max-w-sm"
          >
            <option value="">{t('selectBuildingPlaceholder')}</option>
            {buildings.map((building) => (
              <option key={building.id} value={building.id}>
                {building.name}
              </option>
            ))}
          </select>
          {selectedBuilding && (
            <span className="text-gray-600 text-sm whitespace-nowrap">
              {t('manager')} {selectedBuilding.operator.name}
            </span>
          )}
        </div>
      </div>
      )}

      <div className="mb-6 flex flex-wrap gap-1 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium border-b-2 -mb-px transition-colors text-sm ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {!selectedBuildingId ? (
        <div className="text-center py-12 text-gray-500">{t('pleaseSelectBuilding')}</div>
      ) : isReadingsTab ? (
        <MeterReadingsPanel
          buildingId={selectedBuildingId}
          buildingName={selectedBuilding?.name ?? ''}
          type={readingType}
          billingPeriods={billingPeriods}
          selectedPeriod={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
          t={t}
        />
      ) : (
        <UtilityMetersPanel
          buildingId={selectedBuildingId}
          buildingName={selectedBuilding?.name ?? ''}
          type={readingType}
          t={t}
        />
      )}
    </div>
  );
};

export default BulkFeeEntry;
